import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useRef, useEffect, useState } from "react";
import FeatureBubbles from "./FeatureBubbles";
interface VideoHeroProps {
  videoSrc?: string;
  posterSrc?: string;
}

const VideoHero = ({ videoSrc, posterSrc }: VideoHeroProps) => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  const [videoVerified, setVideoVerified] = useState(false);

  const handleStart = () => {
    navigate('/dashboard');
  };

  // Detect mobile on mount
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Pre-check video availability to prevent console errors
  useEffect(() => {
    if (!videoSrc || isMobile) return;
    
    const controller = new AbortController();
    fetch(videoSrc, { method: 'HEAD', signal: controller.signal })
      .then(res => {
        if (res.ok) {
          setVideoVerified(true);
        } else {
          setHasVideoError(true);
        }
      })
      .catch(() => {
        setHasVideoError(true);
      });
    
    return () => controller.abort();
  }, [videoSrc, isMobile]);

  useEffect(() => {
    const video = videoRef.current;
    if (video && !isMobile && !hasVideoError && videoVerified) {
      const handleLoaded = () => setIsLoaded(true);
      const handleError = () => {
        setHasVideoError(true);
        setIsLoaded(false);
      };
      video.addEventListener('loadeddata', handleLoaded);
      video.addEventListener('error', handleError);
      return () => {
        video.removeEventListener('loadeddata', handleLoaded);
        video.removeEventListener('error', handleError);
      };
    }
  }, [isMobile, hasVideoError, videoVerified]);

  return (
    <section 
      className="min-h-[100dvh] flex flex-col items-center justify-center relative overflow-hidden"
      aria-label="Hero section with video background"
    >
      {/* Video Background - Desktop only, with error fallback */}
      {videoSrc && !isMobile && !hasVideoError && videoVerified ? (
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          src={videoSrc}
          poster={posterSrc}
          autoPlay
          loop
          muted
          playsInline
          aria-hidden="true"
          preload="metadata"
          onError={() => setHasVideoError(true)}
        />
      ) : null}
      {/* Gradient fallback - shown on mobile, video error, video not verified, or when video not provided */}
      {(!videoSrc || isMobile || hasVideoError || !videoVerified) && (
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/80 to-accent" aria-hidden="true" />
      )}
      
      {/* Gradient overlay for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-transparent to-background/60" aria-hidden="true" />

      {/* Desktop floating feature bubbles */}
      <div className="hidden md:block">
        <FeatureBubbles />
      </div>

      <div className="container mx-auto px-4 sm:px-6 text-center relative z-10 flex flex-col items-center justify-center flex-1 py-20">
        {/* Main heading - responsive sizing */}
        <h1 
          className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-display font-bold mb-4 md:mb-6 animate-slide-up leading-tight"
          style={{ animationDelay: '0.1s' }}
        >
          <span className="text-primary-foreground drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] md:drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)]">
            Free Resume Builder
          </span>
          <span className="block text-primary-foreground drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] md:drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)] mt-1 md:mt-2">
            ATS-Optimized with AI
          </span>
        </h1>

        <p 
          className="text-base sm:text-lg md:text-xl lg:text-2xl text-primary-foreground/95 max-w-xs sm:max-w-md md:max-w-2xl mx-auto mb-6 md:mb-10 animate-slide-up font-medium drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)] leading-relaxed"
          style={{ animationDelay: '0.2s' }}
        >
          Free ATS-optimized resume builder with 50+ templates. Get more interviews today.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center animate-slide-up w-full sm:w-auto" style={{ animationDelay: '0.3s' }}>
          <Button 
            size="lg"
            onClick={handleStart} 
            className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-2xl rounded-full px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg font-semibold w-full sm:w-auto touch-manipulation active:scale-95 transition-transform"
            aria-label="Start Your Success Story"
          >
            Start Your Success Story
            <ChevronRight className="ml-2 w-5 h-5" aria-hidden="true" />
          </Button>
        </div>

        {/* Mobile feature bubbles */}
        <div className="md:hidden">
          <FeatureBubbles />
        </div>

        {/* Trust signals */}
        <p 
          className="mt-6 md:mt-8 text-xs sm:text-sm text-primary-foreground/80 animate-slide-up drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
          style={{ animationDelay: '0.8s' }}
        >
          Trusted by 500,000+ job seekers • No credit card required
        </p>
      </div>

      {/* Scroll indicator - hidden on small mobile */}
      <div className="absolute bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 animate-bounce hidden sm:flex flex-col items-center gap-1">
        <span className="text-xs text-primary-foreground/70 drop-shadow-md">Scroll</span>
        <ChevronRight className="w-5 h-5 text-primary-foreground/70 rotate-90" />
      </div>
    </section>
  );
};

export default VideoHero;
